export class Color {
  private r: number;
  private g: number;
  private b: number;
  private a: number;

  constructor(r: number, g: number, b: number, a?: number) {
    this.r = r < 0 ? 0 : r > 255 ? 255 : r;
    this.g = g < 0 ? 0 : g > 255 ? 255 : g;
    this.b = b < 0 ? 0 : b > 255 ? 255 : b;
    this.a = a || 255;
    this.a = this.a < 0 ? 0 : this.a > 255 ? 255 : this.a;
  }
  public static fromHex(hex: string) {
    let cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;

    if (cleanHex.length === 3) {
      cleanHex = cleanHex.split('').map(char => char + char).join('');
    }

    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return new Color(r, g, b);
  }

  public static WHITE() {
    return new Color(255, 255, 255);
  }

  public static BLACK() {
    return new Color(0, 0, 0);
  }

  private luma(color: Color) {
    return (0.2126 * color.r) + (0.7152 * color.g) + (0.0722 * color.b);
  }

  public contrast() {
    return this.luma(this) >= 165 ? Color.BLACK() : Color.WHITE();
  }

  public invert() {
    return new Color(255 - this.r, 255 - this.g, 255 - this.b);
  }

  public toHex() {
    const { r, g, b } = this;
    return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
  }

  public toRgba() {
    return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`;
  }

  private toHsl(): { h: number; s: number; l: number } {
    const r = this.r / 255;
    const g = this.g / 255;
    const b = this.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  private static fromHsl(h: number, s: number, l: number, a: number = 255): Color {
    h /= 360;
    s /= 100;
    l /= 100;

    let r: number, g: number, b: number;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number): number => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return new Color(
      Math.round(r * 255),
      Math.round(g * 255),
      Math.round(b * 255),
      a
    );
  }

  monochromePalette(count: number = 5): Color[] {
    const { h, s, l } = this.toHsl();
    const step = 100 / (count + 1);

    const palette: Color[] = [];
    for (let i = 1; i <= count; i++) {
      const newL = Math.min(100, Math.max(0, i * step)); 
      palette.push(Color.fromHsl(h, s, newL, this.a));
    }
    return palette;
  }
}
